/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

const { width } = Dimensions.get('window');

const AssignmentScreen = () => {
  const navigation = useNavigation();
  const [userData, setUserData] = useState(null);
  const [selectedTab, setSelectedTab] = useState('Active');

  // Mixed color scheme for different assignment types
  const assignmentColors = {
    'Essay': '#f59e0b', // Orange
    'Project': '#10b981', // Green
    'Report': '#3b82f6', // Blue
    'Presentation': '#8b5cf6', // Purple
    'Research': '#ef4444', // Red
    'Case Study': '#06b6d4', // Cyan
    'Lab Work': '#f97316', // Orange variant
    'Design': '#14b8a6', // Teal
  };

  const getColorForType = (type) => {
    return assignmentColors[type] || '#f59e0b';
  };

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      const storedUserData = await AsyncStorage.getItem('userData');
      
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
      } else if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const getUserName = () => {
    if (!userData) return 'Student';
    return userData.full_name || userData.fullName || userData.name || 
           `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || 
           'Student';
  };

  // Sample assignments data
  const assignments = [
    {
      id: '1',
      title: 'Structural Analysis Report',
      course: 'Civil Engineering Fundamentals',
      type: 'Report',
      dueDate: '2024-12-25',
      status: 'Active',
      progress: 65,
      description: 'Analyze the structural integrity of a steel frame building and provide recommendations.',
      instructor: 'Dr. Sarah Johnson',
      maxScore: 100,
      submissionFormat: 'PDF Report + Calculations',
      requirements: ['Minimum 2000 words', 'Include diagrams', 'Reference standards'],
      priority: 'High',
    },
    {
      id: '2',
      title: 'AutoCAD Bridge Design',
      course: 'AutoCAD for Engineers',
      type: 'Project',
      dueDate: '2024-12-30',
      status: 'Active',
      progress: 40,
      description: 'Design a pedestrian bridge using AutoCAD with detailed technical drawings.',
      instructor: 'Prof. Michael Chen',
      maxScore: 100,
      submissionFormat: 'CAD Files + Portfolio',
      requirements: ['2D and 3D drawings', 'Material specifications', 'Cost estimation'],
      priority: 'Medium',
    },
    {
      id: '3',
      title: 'Project Management Case Study',
      course: 'Project Management Essentials',
      type: 'Case Study',
      dueDate: '2025-01-05',
      status: 'Active',
      progress: 20,
      description: 'Analyze a failed construction project and propose alternative management strategies.',
      instructor: 'Dr. Lisa Anderson',
      maxScore: 100,
      submissionFormat: 'Written Analysis + Presentation',
      requirements: ['PRINCE2 methodology', 'Risk analysis', '15-minute presentation'],
      priority: 'Medium',
    },
    {
      id: '4',
      title: 'Safety Protocol Presentation',
      course: 'Health & Safety Fundamentals',
      type: 'Presentation',
      dueDate: '2024-12-20',
      status: 'Submitted',
      progress: 100,
      description: 'Present workplace safety protocols for construction sites.',
      instructor: 'Prof. Robert Taylor',
      maxScore: 100,
      submissionFormat: 'PowerPoint + Demo',
      requirements: ['20 slides maximum', 'Interactive demo', 'Q&A session'],
      priority: 'Low',
      submittedDate: '2024-12-18',
      grade: 'A',
      feedback: 'Excellent presentation with clear safety protocols and engaging demonstration.',
    },
    {
      id: '5',
      title: 'Materials Testing Lab Report',
      course: 'Materials Science Basics',
      type: 'Lab Work',
      dueDate: '2024-12-15',
      status: 'Overdue',
      progress: 80,
      description: 'Conduct tensile strength tests on various materials and analyze results.',
      instructor: 'Dr. Jennifer Lee',
      maxScore: 100,
      submissionFormat: 'Lab Report + Data',
      requirements: ['Test data analysis', 'Statistical calculations', 'Material comparison'],
      priority: 'High',
    },
    {
      id: '6',
      title: 'Python Data Analysis Essay',
      course: 'Python Programming',
      type: 'Essay',
      dueDate: '2024-12-28',
      status: 'Active',
      progress: 30,
      description: 'Write an essay on the applications of Python in engineering data analysis.',
      instructor: 'Prof. Alex Rodriguez',
      maxScore: 100,
      submissionFormat: 'Written Essay + Code Examples',
      requirements: ['1500 words minimum', 'Code snippets', 'Real-world examples'],
      priority: 'Medium',
    },
  ];

  const tabs = ['Active', 'Submitted', 'Overdue'];
  
  const filteredAssignments = assignments.filter(assignment => {
    if (selectedTab === 'Active') {
      return assignment.status === 'Active';
    }
    return assignment.status === selectedTab;
  });

  const handleStartAssignment = (assignment) => {
    Alert.alert(
      'Start Assignment',
      `Start working on "${assignment.title}"?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Start',
          onPress: () => {
            Alert.alert('Assignment Started', `You can now work on "${assignment.title}". Good luck!`);
          },
        },
      ]
    );
  };

  const handleSubmitAssignment = (assignment) => {
    Alert.alert(
      'Submit Assignment',
      `Submit "${assignment.title}" for grading?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Submit',
          onPress: () => {
            Alert.alert('Success', `"${assignment.title}" has been submitted successfully!\n\nYou will receive feedback within 5-7 business days.`);
          },
        },
      ]
    );
  };

  const handleViewFeedback = (assignment) => {
    Alert.alert(
      'Assignment Feedback',
      `${assignment.title}\n\nGrade: ${assignment.grade}\n\nFeedback: ${assignment.feedback}\n\nSubmitted: ${assignment.submittedDate}`,
      [{ text: 'OK' }]
    );
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return '#ef4444';
      case 'Medium': return '#f59e0b';
      case 'Low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return '#3b82f6';
      case 'Submitted': return '#10b981';
      case 'Overdue': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      {tabs.map((tab) => (
        <TouchableOpacity
          key={tab}
          style={[
            styles.tab,
            selectedTab === tab && [styles.activeTab, { backgroundColor: '#f59e0b' }],
          ]}
          onPress={() => setSelectedTab(tab)}
        >
          <Text
            style={[
              styles.tabText,
              selectedTab === tab && styles.activeTabText,
            ]}
          >
            {tab}
          </Text>
          <View style={[
            styles.tabBadge,
            { backgroundColor: selectedTab === tab ? '#ffffff' : '#f59e0b' }
          ]}>
            <Text style={[
              styles.tabBadgeText,
              { color: selectedTab === tab ? '#f59e0b' : '#ffffff' }
            ]}>
              {assignments.filter(a => a.status === tab).length}
            </Text>
          </View>
        </TouchableOpacity>
      ))}
    </View>
  );

  const renderProgressBar = (progress) => (
    <View style={styles.progressContainer}>
      <View style={styles.progressBar}>
        <View style={[styles.progressFill, { width: `${progress}%` }]} />
      </View>
      <Text style={styles.progressText}>{progress}%</Text>
    </View>
  );

  const renderAssignmentCard = (assignment) => {
    const assignmentColor = getColorForType(assignment.type);
    const statusColor = getStatusColor(assignment.status);
    const priorityColor = getPriorityColor(assignment.priority);
    
    return (
      <View key={assignment.id} style={styles.assignmentCard}>
        {/* Assignment Header */}
        <View style={styles.assignmentHeader}>
          <View style={[styles.assignmentIcon, { backgroundColor: assignmentColor }]}>
            <MaterialCommunityIcons 
              name={assignment.type === 'Essay' ? 'file-document-edit' :
                    assignment.type === 'Project' ? 'folder-multiple' :
                    assignment.type === 'Report' ? 'file-chart' :
                    assignment.type === 'Presentation' ? 'presentation' :
                    assignment.type === 'Research' ? 'magnify' :
                    assignment.type === 'Case Study' ? 'briefcase-search' :
                    assignment.type === 'Lab Work' ? 'flask' :
                    assignment.type === 'Design' ? 'drawing' : 'file-document'} 
              size={24} 
              color="#ffffff" 
            />
          </View>
          <View style={styles.assignmentInfo}>
            <Text style={styles.assignmentTitle}>{assignment.title}</Text>
            <Text style={styles.assignmentCourse}>{assignment.course}</Text>
            <Text style={styles.assignmentInstructor}>by {assignment.instructor}</Text>
          </View>
          <View style={styles.statusContainer}>
            <View style={[styles.statusBadge, { backgroundColor: statusColor }]}>
              <Text style={styles.statusText}>{assignment.status}</Text>
            </View>
            <View style={[styles.priorityBadge, { borderColor: priorityColor }]}>
              <Text style={[styles.priorityText, { color: priorityColor }]}>{assignment.priority}</Text>
            </View>
          </View>
        </View>

        {/* Assignment Details */}
        <Text style={styles.assignmentDescription}>{assignment.description}</Text>

        <View style={styles.assignmentMetadata}>
          <View style={styles.metadataRow}>
            <View style={styles.metadataItem}>
              <MaterialCommunityIcons name="calendar-clock" size={16} color="#6b7280" />
              <Text style={styles.metadataText}>Due: {assignment.dueDate}</Text>
            </View>
            <View style={styles.metadataItem}>
              <MaterialCommunityIcons name="file-document" size={16} color="#6b7280" />
              <Text style={styles.metadataText}>{assignment.type}</Text>
            </View>
          </View>
          
          <View style={styles.metadataRow}>
            <View style={styles.metadataItem}>
              <MaterialCommunityIcons name="trophy" size={16} color="#6b7280" />
              <Text style={styles.metadataText}>Max Score: {assignment.maxScore}</Text>
            </View>
            <View style={styles.metadataItem}>
              <MaterialCommunityIcons name="file-upload" size={16} color="#6b7280" />
              <Text style={styles.metadataText}>{assignment.submissionFormat}</Text>
            </View>
          </View>
        </View>

        {/* Progress Bar (for active assignments) */}
        {assignment.status === 'Active' && renderProgressBar(assignment.progress)}

        {/* Requirements */}
        <View style={styles.requirementsContainer}>
          <Text style={styles.requirementsTitle}>Requirements:</Text>
          {assignment.requirements.map((requirement, index) => (
            <View key={index} style={styles.requirementItem}>
              <MaterialCommunityIcons name="check-circle-outline" size={14} color={assignmentColor} />
              <Text style={styles.requirementText}>{requirement}</Text>
            </View>
          ))}
        </View>

        {/* Actions */}
        <View style={styles.assignmentActions}>
          {assignment.status === 'Active' && (
            <>
              <TouchableOpacity
                style={[styles.actionButton, styles.startButton, { backgroundColor: assignmentColor }]}
                onPress={() => handleStartAssignment(assignment)}
              >
                <MaterialCommunityIcons name="play" size={16} color="#ffffff" />
                <Text style={styles.actionButtonText}>Continue</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.actionButton, styles.submitButton, { borderColor: assignmentColor }]}
                onPress={() => handleSubmitAssignment(assignment)}
              >
                <MaterialCommunityIcons name="upload" size={16} color={assignmentColor} />
                <Text style={[styles.actionButtonText, { color: assignmentColor }]}>Submit</Text>
              </TouchableOpacity>
            </>
          )}
          
          {assignment.status === 'Submitted' && (
            <TouchableOpacity
              style={[styles.actionButton, styles.feedbackButton, { backgroundColor: '#10b981' }]}
              onPress={() => handleViewFeedback(assignment)}
            >
              <MaterialCommunityIcons name="comment-text" size={16} color="#ffffff" />
              <Text style={styles.actionButtonText}>View Feedback</Text>
            </TouchableOpacity>
          )}
          
          {assignment.status === 'Overdue' && (
            <TouchableOpacity
              style={[styles.actionButton, styles.urgentButton, { backgroundColor: '#ef4444' }]}
              onPress={() => handleSubmitAssignment(assignment)}
            >
              <MaterialCommunityIcons name="alert" size={16} color="#ffffff" />
              <Text style={styles.actionButtonText}>Submit Now</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderSummaryStats = () => (
    <View style={styles.summaryCard}>
      <Text style={styles.summaryTitle}>Assignment Overview</Text>
      <Text style={styles.summarySubtitle}>Hello, {getUserName()}!</Text>
      
      <View style={styles.statsGrid}>
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#3b82f6' }]}>
            <MaterialCommunityIcons name="file-document" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#3b82f6' }]}>
            {assignments.filter(a => a.status === 'Active').length}
          </Text>
          <Text style={styles.statLabel}>Active</Text>
        </View>
        
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#10b981' }]}>
            <MaterialCommunityIcons name="check-circle" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#10b981' }]}>
            {assignments.filter(a => a.status === 'Submitted').length}
          </Text>
          <Text style={styles.statLabel}>Submitted</Text>
        </View>
        
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#ef4444' }]}>
            <MaterialCommunityIcons name="clock-alert" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#ef4444' }]}>
            {assignments.filter(a => a.status === 'Overdue').length}
          </Text>
          <Text style={styles.statLabel}>Overdue</Text>
        </View>
        
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#f59e0b' }]}>
            <MaterialCommunityIcons name="trophy" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#f59e0b' }]}>
            {assignments.filter(a => a.grade === 'A').length}
          </Text>
          <Text style={styles.statLabel}>A Grades</Text>
        </View>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#f9fafb" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#f59e0b" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Assignments</Text>
        <TouchableOpacity style={styles.menuButton}>
          <MaterialCommunityIcons name="dots-vertical" size={24} color="#6b7280" />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {renderSummaryStats()}
        {renderTabBar()}
        
        <View style={styles.assignmentsSection}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              {selectedTab} Assignments
            </Text>
            <Text style={styles.sectionCount}>{filteredAssignments.length} assignments</Text>
          </View>
          
          {filteredAssignments.length > 0 ? (
            filteredAssignments.map(renderAssignmentCard)
          ) : (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="file-document" size={64} color="#d1d5db" />
              <Text style={styles.emptyStateTitle}>No {selectedTab} Assignments</Text>
              <Text style={styles.emptyStateText}>
                {selectedTab === 'Active' 
                  ? 'You have no active assignments at the moment.' 
                  : selectedTab === 'Submitted'
                  ? 'You haven\'t submitted any assignments yet.'
                  : 'Great! You have no overdue assignments.'}
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#f59e0b',
  },
  menuButton: {
    padding: 5,
  },
  container: {
    flex: 1,
  },
  summaryCard: {
    backgroundColor: '#fff',
    margin: 15,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  summaryTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  summarySubtitle: {
    fontSize: 16,
    color: '#6b7280',
    marginBottom: 20,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    marginHorizontal: 15,
    marginBottom: 15,
    borderRadius: 12,
    padding: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  activeTab: {
    // backgroundColor set dynamically
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
    marginRight: 6,
  },
  activeTabText: {
    color: '#ffffff',
    fontWeight: '600',
  },
  tabBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabBadgeText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  assignmentsSection: {
    paddingHorizontal: 15,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
  },
  sectionCount: {
    fontSize: 14,
    color: '#6b7280',
  },
  assignmentCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  assignmentHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  assignmentIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  assignmentInfo: {
    flex: 1,
  },
  assignmentTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 2,
  },
  assignmentCourse: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 2,
  },
  assignmentInstructor: {
    fontSize: 12,
    color: '#9ca3af',
  },
  statusContainer: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 4,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#ffffff',
  },
  priorityBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
  },
  priorityText: {
    fontSize: 9,
    fontWeight: '500',
  },
  assignmentDescription: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
    marginBottom: 12,
  },
  assignmentMetadata: {
    marginBottom: 12,
  },
  metadataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  metadataItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  metadataText: {
    fontSize: 12,
    color: '#6b7280',
    marginLeft: 6,
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  progressBar: {
    flex: 1,
    height: 6,
    backgroundColor: '#e5e7eb',
    borderRadius: 3,
    marginRight: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#f59e0b',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  requirementsContainer: {
    marginBottom: 16,
  },
  requirementsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  requirementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  requirementText: {
    fontSize: 12,
    color: '#6b7280',
    marginLeft: 6,
    flex: 1,
  },
  assignmentActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    flex: 1,
    marginHorizontal: 2,
    justifyContent: 'center',
  },
  startButton: {
    // backgroundColor set dynamically
  },
  submitButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  feedbackButton: {
    // backgroundColor set dynamically
  },
  urgentButton: {
    // backgroundColor set dynamically
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 4,
    color: '#ffffff',
  },
  emptyState: {
    alignItems: 'center',
    padding: 40,
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 15,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#374151',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default AssignmentScreen;

